import {
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import escapeRegExp from 'lodash/escapeRegExp';
import CloseIcon from '@paradigm/design-system/src/assets/CloseIcon';
import SearchIcon from '@paradigm/design-system/src/assets/SearchIcon';
import { useOutsideClickHandler } from '#/hooks/useOutsideClickHandler';

import { blackPearl, paradigmBlue } from '@paradigm/design-system/src/colors';

import Button from '@paradigm/design-system/src/components/Button';
import { stopPropagation } from '../drfq/common';

export interface SearchOption {
  readonly value: string;
  readonly description: string;
  readonly key: string;
  readonly isRemovable: boolean;
}

export interface SearchProps<T extends SearchOption> {
  readonly data: T[];
  readonly enableAutoFocus?: boolean;
  readonly placeholder?: string;
  readonly onSelect?: (value: T) => void;
  readonly onRemove?: (value: T) => void;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  readonly onChange?: (value: string) => void;
  readonly onRemoveAll?: () => void;
}

interface PropsInput extends InputHTMLAttributes<HTMLInputElement> {
  readonly enableAutoFocus?: boolean;
}

export function Search<T extends SearchOption>(props: SearchProps<T>) {
  const { data } = props;
  const [focused, setFocused] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const onSelect = (e: React.SyntheticEvent<HTMLLIElement>, item: T) => {
    e.stopPropagation();
    props.onSelect?.(item);
    props.onBlur?.();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = e.currentTarget.value;
    setSearch(searchText);
    props.onChange?.(searchText);
  };

  const clearAll = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSearch('');
    props.onChange?.('');
  };

  const onFocus = () => {
    setFocused(true);
    props.onFocus?.();
  };

  const onBlur = () => {
    setFocused(false);
    props.onBlur?.();
  };

  const onRemove = (e: React.SyntheticEvent<HTMLDivElement>, item: T) => {
    e.stopPropagation();
    props.onRemove?.(item);
  };

  useOutsideClickHandler(ref, onBlur);

  const items = useMemo(() => {
    if (search === '') return data;
    return data.filter((item) =>
      item.description.match(new RegExp(escapeRegExp(search), 'giu')),
    );
  }, [data, search]);

  return (
    <AnchorContainer>
      <Container ref={ref} onClick={stopPropagation()}>
        <SearchBox>
          <SearchInput
            enableAutoFocus={props.enableAutoFocus ?? false}
            placeholder={props.placeholder ?? 'Search'}
            onChange={onChange}
            onFocus={onFocus}
            value={search}
          />
          {props.onRemoveAll !== undefined ? (
            <ClearButton ariaLabel="close all" onClick={props.onRemoveAll}>
              Close All
            </ClearButton>
          ) : (
            <ClearButton ariaLabel="clear all" onClick={clearAll}>
              Clear All
            </ClearButton>
          )}
        </SearchBox>
        <Dropdown>
          {focused &&
            items.map((item, index) => (
              <DropdownItem
                index={index}
                key={item.key}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (['Space', 'Enter'].includes(e.key)) {
                    onSelect(e, item);
                  }
                }}
                onClick={(e) => onSelect(e, item)}
              >
                {item.description}
                {item.isRemovable && (
                  <RemoveContainer onClick={(e) => onRemove(e, item)}>
                    <CloseIcon
                      width={8}
                      height={8}
                      color="#959EA9"
                      className="close-icon"
                    />
                  </RemoveContainer>
                )}
              </DropdownItem>
            ))}
        </Dropdown>
      </Container>
    </AnchorContainer>
  );
}

export const SearchInput = (props: PropsInput) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.enableAutoFocus !== false) ref.current?.focus();
  }, [props.enableAutoFocus, ref]);

  return (
    <SearchInputContainer>
      <SearchIconContainer>
        <SearchIcon width={14} height={14} />
      </SearchIconContainer>
      <Input {...props} ref={ref} />
    </SearchInputContainer>
  );
};

const RemoveContainer = styled.div`
  cursor: pointer;
  padding: 4px;
`;

const ClearButton = styled(Button).attrs({ variant: 'link', size: 'small' })`
  margin-top: 8px;
  white-space: nowrap;
`;

const SearchBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px;
`;

const AnchorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Dropdown = styled.ul`
  width: 100%;
  height: 100%;
  overflow: auto;
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const Container = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  max-height: 80vh;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  cursor: pointer;
  background-color: ${blackPearl};
  box-shadow: -20px 10px 30px 6px rgba(0, 0, 0, 0.1);
  z-index: 999;
`;

const DropdownItem = styled.li<{ index: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 44px;
  width: 100%;
  padding: 0px 16px;
  background-color: ${(props) =>
    props.index % 2 === 0 ? `rgba(5,21,36,0.3)` : `transparent`};
  box-shadow: inset 0 -1px 0 0 #27364c;
  font-size: 13px;
  color: #cdd2d7;
  font-family: 'IBM Plex Sans';
  font-weight: 600;
  line-height: 16px;
  outline: none;

  :focus {
    border: 1px solid ${paradigmBlue};
  }

  &:hover {
    background-color: rgba(5, 21, 36, 0.8);
  }
`;

const SearchIconContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
`;

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 0px 10px;
  border-radius: 16px;
  height: 32px;
  background-color: #0f3155;
`;

const Input = styled.input`
  width: 100%;
  height: 100%;
  font-size: 14px;
  color: white;
  border: none;
  background: none;
  outline: none;
  margin-left: 4px;

  ::placeholder {
    color: #8492a3;
  }
`;
